import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Alert, Button, Input, Modal, Space, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import { toast } from "react-toastify";
import PopularSearchDrawer from "./PopularSearchDrawer";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function PopularSearchesPage() {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const fetchPage = useCallback(async (pag = pagination) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pag.current - 1, size: pag.pageSize };
      const res = await olreadiaService.popularSearches.list(params);
      const content = Array.isArray(res?.content) ? res.content : [];
      setItems(content);
      setPagination((prev) => ({
        ...prev,
        current: pag.current,
        pageSize: pag.pageSize,
        total: res?.totalElements ?? content.length,
      }));
    } catch (err) {
      setError(toMessage(err, "Failed to load popular searches"));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current]);

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((item) =>
      [item?.label, item?.searchQuery]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, query]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await olreadiaService.popularSearches.update(editing.id, values, actorId);
        toast.success("Popular search updated");
      } else {
        await olreadiaService.popularSearches.create(values, actorId);
        toast.success("Popular search created");
      }
      setDrawerOpen(false);
      await fetchPage();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save popular search"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    setRecordToDelete(record);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    setDeletingId(recordToDelete.id);
    try {
      await olreadiaService.popularSearches.delete(recordToDelete.id);
      toast.success("Popular search removed");
      await fetchPage();
    } catch (err) {
      toast.error(toMessage(err, "Failed to delete popular search"));
    } finally {
      setDeletingId(null);
      setDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value || "-"} placement="topLeft">
          <span style={{ fontWeight: 600 }}>{value || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Search Query",
      dataIndex: "searchQuery",
      key: "searchQuery",
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "Ranking",
      dataIndex: "ranking",
      key: "ranking",
      width: 90,
      sorter: (a, b) => (a.ranking || 0) - (b.ranking || 0),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 90,
      render: (value) =>
        value ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(record);
              setDrawerOpen(true);
            }}
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === record.id}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="search"
          title="Popular Searches"
          subtitle="Manage popular search queries."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchPage()} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            Add Search
          </Button>
        </Space>
      </div>

      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
        <Input.Search
          allowClear
          placeholder="Search by label or query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", maxWidth: 320 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={<Button size="small" onClick={() => fetchPage()}>Retry</Button>}
        />
      ) : null}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredItems}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={(pag) => fetchPage({ current: pag.current, pageSize: pag.pageSize })}
        locale={{ emptyText: "No popular searches found." }}
      />

      <PopularSearchDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={saving}
        initialValues={editing}
      />

      <Modal
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        title="Remove popular search?"
        okText="Remove"
        okButtonProps={{ danger: true, loading: deletingId === recordToDelete?.id }}
        onOk={confirmDelete}
        zIndex={2000}
      >
        <p>This will permanently delete this popular search.</p>
      </Modal>
    </div>
  );
}
